<template>
  <div class="transactions">
    <div class="page-header">
      <h1>Transactions</h1>
      <div class="header-actions">
        <el-button @click="$router.push('/dashboard')">
          <el-icon><DataBoard /></el-icon>
          Dashboard
        </el-button>
        <el-button type="primary" @click="$router.push('/transactions/new')">
          <el-icon><Plus /></el-icon>
          Add Transaction
        </el-button>
      </div>
    </div>

    <!-- Filters -->
    <el-card class="filters-card">
      <el-form :model="filters" inline>
        <el-form-item label="Type">
          <el-select v-model="filters.type" placeholder="All Types" clearable>
            <el-option label="Sales" value="sale" />
            <el-option label="Costs" value="cost" />
          </el-select>
        </el-form-item>
        
        <el-form-item label="Category">
          <el-input v-model="filters.category" placeholder="Category" clearable />
        </el-form-item>
        
        <el-form-item label="Date Range">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="to"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            @change="handleDateRangeChange"
          />
        </el-form-item>
        
        <el-form-item label="Payment Method">
          <el-select v-model="filters.paymentMethod" placeholder="All Methods" clearable>
            <el-option label="Cash" value="cash" />
            <el-option label="Card" value="card" />
            <el-option label="Transfer" value="transfer" />
            <el-option label="Other" value="other" />
          </el-select>
        </el-form-item>
        
        <el-form-item>
          <el-button @click="clearFilters">Clear</el-button>
          <el-button type="primary" @click="applyFilters">Apply</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Transactions Table -->
    <el-card class="transactions-card">
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="10" animated />
      </div>
      
      <div v-else-if="transactions.length === 0" class="empty-state">
        <el-icon><Document /></el-icon>
        <h3>No transactions found</h3>
        <p>Start by adding your first transaction</p>
        <el-button type="primary" @click="$router.push('/transactions/new')">
          Add Transaction
        </el-button>
      </div>
      
      <el-table v-else :data="transactions" style="width: 100%">
        <el-table-column prop="type" label="Type" width="100">
          <template #default="{ row }">
            <el-tag :type="row.type === 'sale' ? 'success' : 'danger'">
              {{ row.type === 'sale' ? 'Sale' : 'Cost' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="description" label="Description" />
        
        <el-table-column prop="category" label="Category" width="120" />
        
        <el-table-column prop="amount" label="Amount" width="120">
          <template #default="{ row }">
            <span :class="{ 'positive': row.type === 'sale', 'negative': row.type === 'cost' }">
              {{ row.type === 'sale' ? '+' : '-' }}${{ formatNumber(row.amount) }}
            </span>
          </template>
        </el-table-column>
        
        <el-table-column prop="date" label="Date" width="120">
          <template #default="{ row }">
            {{ formatDate(row.date) }}
          </template>
        </el-table-column>
        
        <el-table-column prop="paymentMethod" label="Payment" width="100">
          <template #default="{ row }">
            <el-tag size="small">{{ row.paymentMethod }}</el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="Actions" width="150">
          <template #default="{ row }">
            <el-button size="small" @click="editTransaction(row.id)">
              <el-icon><Edit /></el-icon>
            </el-button>
            <el-button size="small" type="danger" @click="deleteTransaction(row.id)">
              <el-icon><Delete /></el-icon>
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination -->
      <div class="pagination-container" v-if="pagination.pages > 1">
        <el-pagination
          v-model:current-page="currentPage"
          :page-size="pagination.limit"
          :total="pagination.total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Plus, Edit, Delete, Document, DataBoard } from '@element-plus/icons-vue'
import { toast } from 'vue3-toastify'

export default {
  name: 'Transactions',
  components: {
    Plus,
    Edit,
    Delete,
    Document,
    DataBoard
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    
    const loading = ref(false)
    const currentPage = ref(1)
    const dateRange = ref([])
    
    const filters = ref({
      type: '',
      category: '',
      startDate: '',
      endDate: '',
      paymentMethod: '',
      sortBy: 'date',
      sortOrder: 'desc'
    })

    const transactions = computed(() => store.getters['transactions/transactions'])
    const pagination = computed(() => store.getters['transactions/pagination'])

    const formatNumber = (num) => {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num)
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    const loadTransactions = async (page = 1) => {
      try {
        loading.value = true
        await store.dispatch('transactions/fetchTransactions', { page })
      } catch (error) {
        toast.error('Failed to load transactions')
      } finally {
        loading.value = false
      }
    }

    const applyFilters = () => {
      store.dispatch('transactions/setFilters', filters.value)
      loadTransactions(1)
    }

    const clearFilters = () => {
      store.dispatch('transactions/clearFilters')
      filters.value = {
        type: '',
        category: '',
        startDate: '',
        endDate: '',
        paymentMethod: '',
        sortBy: 'date',
        sortOrder: 'desc'
      }
      dateRange.value = []
      loadTransactions(1)
    }

    const handleDateRangeChange = (range) => {
      if (range && range.length === 2) {
        filters.value.startDate = range[0]
        filters.value.endDate = range[1]
      } else {
        filters.value.startDate = ''
        filters.value.endDate = ''
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      loadTransactions(page)
    }

    const editTransaction = (id) => {
      router.push(`/transactions/${id}/edit`)
    }

    const deleteTransaction = async (id) => {
      try {
        await store.dispatch('transactions/deleteTransaction', id)
        toast.success('Transaction deleted successfully')
        loadTransactions(currentPage.value)
      } catch (error) {
        toast.error('Failed to delete transaction')
      }
    }

    onMounted(() => {
      loadTransactions()
    })

    return {
      loading,
      transactions,
      pagination,
      filters,
      dateRange,
      currentPage,
      formatNumber,
      formatDate,
      applyFilters,
      clearFilters,
      handleDateRangeChange,
      handlePageChange,
      editTransaction,
      deleteTransaction
    }
  }
}
</script>

<style scoped>
.transactions {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
  color: #2c3e50;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filters-card {
  margin-bottom: 20px;
  border-radius: 8px;
}

.transactions-card {
  border-radius: 8px;
}

.loading-container {
  padding: 20px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.empty-state .el-icon {
  font-size: 4rem;
  margin-bottom: 20px;
  color: #c0c4cc;
}

.empty-state h3 {
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  color: #2c3e50;
}

.empty-state p {
  margin: 0 0 20px 0;
  color: #7f8c8d;
}

.positive {
  color: #67c23a;
  font-weight: 600;
}

.negative {
  color: #f56c6c;
  font-weight: 600;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* Responsive design */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .el-form--inline .el-form-item {
    margin-right: 0;
    margin-bottom: 10px;
  }
}
</style> 